/**
 * Research prompt template + default category angles.
 * Seeded into DB on boot; runtime reads meta / research_categories so edits
 * never require a code change. Only {{ANCHORS}} and {{CATEGORIES}} are
 * substituted at copy time — everything else is literal prompt text.
 */

export const RESEARCH_PROMPT_TEMPLATE_V1 = `You are a research assistant for a short-form video series about music,
made from an ethnomusicology perspective: music as a window into culture,
history, language, migration, and meaning — not chart trivia.

RESEARCH SUBJECT (verified anchors — do not contradict these; if your
sources conflict with an anchor, flag the conflict instead of overriding):

{{ANCHORS}}

RESEARCH ANGLES — answer only those where real information exists. Suggested
angles below; if you find something compelling that fits none of them, use a
new short lowercase category name of your own and include it the same way:

{{CATEGORIES}}

RULES:
- Every claim needs a source you actually consulted. Prefer academic or
  journalistic writing, label/reissue liner notes, artist interviews;
  Wikipedia as a pointer to better sources.
- If sources disagree, say so inside the claim's conflict field.
- If little or nothing is documented, say so honestly in "coverage".
  DO NOT fill gaps with plausible-sounding invention. "Little documented"
  is a useful result.
- Never invent chart positions, sales figures, nicknames, or epithets.
- Confidence per claim: "high" (multiple solid sources), "medium" (one
  decent source), "low" (uncertain, disputed, or inferred).
- Register: a knowledgeable friend, not an encyclopedia. Each claim's
  "text" must be one sentence speakable aloud in ~8 seconds. Use "detail"
  for supporting depth.
- 4 to 10 claims. Fewer good ones beat many weak ones.

YOUR RESPONSE HAS EXACTLY TWO PARTS.

PART 1 — a readable summary in this format:

RESEARCH: <track> — <artist>
Coverage: <rich|moderate|sparse|little documented> — <one line>

THE STORY (in suggested order)
<numbered claims: [category · confidence] text, then "→ source: url">

WORTH DIGGING
<bulleted unverifiable leads>

PART 2 — after the exact line "— JSON for CoyKoi below —", a single fenced
json code block containing the same claims in this exact schema:

{
  "subject": { "track": "string", "artist": "string" },
  "coverage": "rich" | "moderate" | "sparse" | "little_documented",
  "coverage_note": "one sentence on how well-documented this subject is",
  "claims": [
    {
      "category": "short lowercase string — a suggested angle or your own",
      "text": "one speakable sentence",
      "detail": "1-2 supporting sentences, or null",
      "confidence": "high" | "medium" | "low",
      "sources": ["url or citation string"],
      "conflict": null
    }
  ],
  "suggested_arc": ["category", "category"],
  "gaps": ["unverifiable but promising leads for further digging"]
}

Nothing after the closing fence. No commentary between Part 1 and Part 2.

EXAMPLE of a complete correct response (different subject, for format
reference only — do not reuse its content):

RESEARCH: Pesse Mi Buntare — Yta Jourias
Coverage: sparse — little English-language writing exists; most information
comes from reissue liner notes around the 2016-2019 Togo revival compilations.

THE STORY (in suggested order)
1. [moment · high] The album's title, Pouvoir Noir — Black Power — landed
   just 15 years after Togo's independence from France.
   → source: release itself; general historical record
2. [people · high] Yta Jourias was born Germaine Adjovi Amékou in Tsévié,
   Togo.
   → source: https://hotcasarecords.bandcamp.com/album/togo-soul-70
3. [place · high] The album was recorded in Paris for Sonafric, a label
   built to distribute Francophone African artists.
   → source: https://www.discogs.com/label/60613-Sonafric
4. [language · medium] Across the album's eight tracks she sings in Ewe,
   French, Hausa, Bambara, Amina, and Arabic.
   → source: Pouvoir Noir reissue notes

WORTH DIGGING
- What the lyrics are actually about — no translation found
- Her career after Pouvoir Noir; whether she recorded again

— JSON for CoyKoi below —

\`\`\`json
{
  "subject": { "track": "Pesse Mi Buntare", "artist": "Yta Jourias" },
  "coverage": "sparse",
  "coverage_note": "Little English-language writing exists; most information comes from reissue liner notes around the 2016-2019 Togo revival compilations.",
  "claims": [
    {
      "category": "moment",
      "text": "The album's title, Pouvoir Noir — Black Power — landed just 15 years after Togo's independence from France.",
      "detail": "A Togolese woman recording 'Black Power' in Paris, on a French label, in 1975, is a political statement folded inside a commercial release.",
      "confidence": "high",
      "sources": ["release itself; general historical record"],
      "conflict": null
    },
    {
      "category": "people",
      "text": "Yta Jourias was born Germaine Adjovi Amékou in Tsévié, Togo.",
      "detail": null,
      "confidence": "high",
      "sources": ["https://hotcasarecords.bandcamp.com/album/togo-soul-70"],
      "conflict": null
    },
    {
      "category": "place",
      "text": "The album was recorded in Paris for Sonafric, a label built to distribute Francophone African artists.",
      "detail": "Togolese artist, Parisian studio, French label serving African markets — the record traces the post-colonial distribution route of Francophone African pop.",
      "confidence": "high",
      "sources": ["https://www.discogs.com/label/60613-Sonafric"],
      "conflict": null
    },
    {
      "category": "language",
      "text": "Across the album's eight tracks she sings in Ewe, French, Hausa, Bambara, Amina, and Arabic.",
      "detail": "Local, colonial, and trade languages side by side made the album legible across borders.",
      "confidence": "medium",
      "sources": ["Pouvoir Noir reissue notes"],
      "conflict": null
    }
  ],
  "suggested_arc": ["moment", "people", "place", "language"],
  "gaps": [
    "What the lyrics are actually about — no translation found",
    "Her career after Pouvoir Noir; whether she recorded again"
  ]
}
\`\`\``;

/**
 * Continuation clipboard — same ingestible JSON shape, but dig-only.
 * Placeholders: {{ANCHORS}}, {{CATEGORIES}}. Seeded into meta; admin-editable.
 */
export const RESEARCH_CONTINUE_PROMPT_TEMPLATE_V1 = `You are continuing research for a short-form video series about music,
from an ethnomusicology perspective: music as a window into culture,
history, language, migration, and meaning — not chart trivia.

CONTEXT — this is a FOLLOW-UP, not a first-pass survey. An initial research
pass may already live in CoyKoi. A human will tell you what to dig into
next (one category, several claims in the same category, a narrow question,
etc.). Do NOT re-cover every research angle. Do NOT pad unused categories.

SUBJECT (verified anchors — do not contradict these; if sources conflict
with an anchor, flag the conflict instead of overriding):

{{ANCHORS}}

CATEGORY VOCABULARY — optional labels for claims. Use only what matches
what the human asks. One category may appear many times. New short
lowercase names are fine if none fit:

{{CATEGORIES}}

CONTINUATION RULES:
- Research ONLY what the human asks in this chat. One claim is valid.
  Five claims in a single category are valid. A mix is valid when asked.
- Do not invent filler for angles they did not ask about.
- Every claim needs a source you actually consulted. Prefer academic or
  journalistic writing, label/reissue liner notes, artist interviews;
  Wikipedia as a pointer to better sources.
- If sources disagree, say so inside the claim's conflict field.
- Never invent chart positions, sales figures, nicknames, or epithets.
- Confidence: "high" | "medium" | "low". Register: a knowledgeable friend.
  Each claim "text" = one sentence speakable aloud in ~8 seconds; use
  "detail" for supporting depth.
- Output must be ingestible by CoyKoi (same JSON schema as the first pass).

YOUR RESPONSE HAS EXACTLY TWO PARTS.

PART 1 — a readable summary in this format:

RESEARCH: <track> — <artist>
Coverage: <rich|moderate|sparse|little documented> — <one line on THIS dig>

THE STORY (order for this dig only)
<numbered claims: [category · confidence] text, then "→ source: url">

WORTH DIGGING
<bulleted unverifiable leads from this dig, or "(none)">

PART 2 — after the exact line "— JSON for CoyKoi below —", a single fenced
json code block with this schema (claims may be 1..N; omit unused angles):

{
  "subject": { "track": "string", "artist": "string" },
  "coverage": "rich" | "moderate" | "sparse" | "little_documented",
  "coverage_note": "one sentence about this dig's documentation",
  "claims": [
    {
      "category": "short lowercase string",
      "text": "one speakable sentence",
      "detail": "1-2 supporting sentences, or null",
      "confidence": "high" | "medium" | "low",
      "sources": ["url or citation string"],
      "conflict": null
    }
  ],
  "suggested_arc": ["category"],
  "gaps": ["optional leads"]
}

Nothing after the closing fence. No commentary between Part 1 and Part 2.

EXAMPLE of a continuation response (narrow dig — NOT a full category menu):

RESEARCH: Pesse Mi Buntare — Yta Jourias
Coverage: sparse — no published translation; meaning inferred from title
and surrounding album context only.

THE STORY (order for this dig only)
1. [language · low] The title Pesse Mi Buntare is remembered in reissue
   circles as an Ewe-language phrase, but no authoritative gloss was found
   in English-language sources consulted.
   → source: Africa Seven / reissue commentary (no lexicon cited)
2. [language · medium] Elsewhere on Pouvoir Noir she moves between Ewe,
   French, and several West African languages, so a single-language lyric
   reading of this track would be atypical for the album.
   → source: Pouvoir Noir reissue notes

WORTH DIGGING
- A speaker of Ewe who can gloss the title and chorus from the recording

— JSON for CoyKoi below —

\`\`\`json
{
  "subject": { "track": "Pesse Mi Buntare", "artist": "Yta Jourias" },
  "coverage": "sparse",
  "coverage_note": "No published translation; meaning inferred from title and album context only.",
  "claims": [
    {
      "category": "language",
      "text": "The title Pesse Mi Buntare is remembered in reissue circles as an Ewe-language phrase, but no authoritative gloss was found in English-language sources consulted.",
      "detail": null,
      "confidence": "low",
      "sources": ["Africa Seven / reissue commentary (no lexicon cited)"],
      "conflict": null
    },
    {
      "category": "language",
      "text": "Elsewhere on Pouvoir Noir she moves between Ewe, French, and several West African languages, so a single-language lyric reading of this track would be atypical for the album.",
      "detail": null,
      "confidence": "medium",
      "sources": ["Pouvoir Noir reissue notes"],
      "conflict": null
    }
  ],
  "suggested_arc": ["language"],
  "gaps": [
    "A speaker of Ewe who can gloss the title and chorus from the recording"
  ]
}
\`\`\`

---
STOP AFTER READING THIS PROMPT. Before researching, ask the human what
they want to dig into next: which category (or free question), roughly how
many claims, and any focus. Wait for their answer. Then research and return
Part 1 + Part 2 only for that ask.`;

/** Mini schema reminder for "copy fix prompt" after a failed paste. */
export const RESEARCH_JSON_SCHEMA_REMINDER = `{
  "subject": { "track": "string", "artist": "string" },
  "coverage": "rich" | "moderate" | "sparse" | "little_documented",
  "coverage_note": "one sentence",
  "claims": [
    {
      "category": "short lowercase string",
      "text": "one speakable sentence",
      "detail": "string or null",
      "confidence": "high" | "medium" | "low",
      "sources": ["url or citation"],
      "conflict": null
    }
  ],
  "suggested_arc": ["category"],
  "gaps": ["optional strings"]
}`;

/**
 * Build a clipboard prompt the human can paste back into their AI chat
 * after CoyKoi rejected a research JSON paste.
 */
export function buildResearchFixPrompt(validationMessage: string): string {
  const detail = validationMessage.trim() || "(no field detail from the parser)";
  return `CoyKoi rejected the last research JSON paste. Fix the JSON only —
do not redo a full multi-category survey unless the human asks.

Parser / validation problem:
${detail}

Return ONLY a corrected response CoyKoi can ingest:

1) Optional short readable summary (same style as before), then the exact
   line: — JSON for CoyKoi below —
2) One fenced \`\`\`json block matching this schema.

Claims may be 1..N. Same category may repeat. Include only claims that
belong to this dig — do not pad every research angle.

Schema:
${RESEARCH_JSON_SCHEMA_REMINDER}

Nothing after the closing fence.`;
}

export const RESEARCH_BUILTIN_CATEGORIES = [
  {
    name: "tradition",
    question:
      "What musical tradition, lineage, or scene does this come from, and what was happening in it at that moment?",
  },
  {
    name: "language",
    question:
      "What language(s), and is the choice meaningful (colonial vs local, code-switching, lingua franca)?",
  },
  {
    name: "instruments",
    question: "Notable instruments/production and what they signify locally.",
  },
  {
    name: "place",
    question:
      "What geographic / migration / diaspora route does this record trace (origin → studio → label → audience)?",
  },
  {
    name: "moment",
    question: "Political/social/historical context of the year and place.",
  },
  {
    name: "people",
    question:
      "Humanly compelling facts about the artist relevant to this recording (short; no full biography).",
  },
  {
    name: "afterlife",
    question:
      "Reissues, rediscovery, sampling; who is rediscovering it and why, if known.",
  },
] as const;

/** Rarity boost so researched ideas pin to the top of Ideas. */
export const RESEARCH_IDEA_SCORE_BOOST = 10_000;
